import { Controller, FieldErrors, UseControllerProps } from '@graphcommerce/ecommerce-ui'
import { ProductInterface } from '@graphcommerce/graphql-mesh'
import { Selected } from '@graphcommerce/magento-product-configurable/ConfigurableContext/ConfigurableContext'
import { SwatchSize } from '@graphcommerce/magento-product-configurable/Swatches/types'

import { BaseTextFieldProps, SxProps } from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import { UseFormSetValue } from 'react-hook-form'
import { useConfigurableContext } from '../ConfigurableContext/ConfigurableContext'

export type ConfigurableOptionsInputProps = {
  sku: string
  product: Pick<ProductInterface, 'name'>
  errors?: FieldErrors
  size?: SwatchSize
  sx?: SxProps
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setValue?: UseFormSetValue<any>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
} & UseControllerProps<any> &
  Pick<BaseTextFieldProps, 'FormHelperTextProps' | 'helperText'> & {
    optionEndLabels?: Record<string, React.ReactNode>
  }

export function ConfigurableOptionsInput(props: ConfigurableOptionsInputProps) {
  const { sku, FormHelperTextProps, errors, name, setValue, ...controlProps } = props

  const { options, selection, select } = useConfigurableContext(sku)
  const [initialSelectionsInit, setInitialSelectionsInit] = useState(false)
  const isInitialMount = useRef(true)

  // This may not always work, coz some combinations may not exist, but in our case we don't have inventory
  useEffect(() => {
    const initialSelection = {}
    options?.forEach((option) => {
      if (option?.attribute_code) {
        initialSelection[option.attribute_code] = option.values?.at(0)?.uid || null
      }
    })
    select((prev) => ({ ...prev, ...initialSelection } as Selected))
    setInitialSelectionsInit(true)
  }, [select, options])

  // Sync external selection changes (e.g. from ProductConfigurationGrid) into form fields
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }
    if (!setValue || !initialSelectionsInit) return
    options?.forEach((option) => {
      if (option?.attribute_code && selection[option.attribute_code]) {
        setValue(`${name}[${option.attribute_code}]`, selection[option.attribute_code])
      }
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection])

  const optionsSorted = Array.isArray(options)
    ? [...options]?.sort((o1, o2) => ((o1?.position || 0) > (o2?.position || 0) ? 1 : -1))
    : []
  if (!initialSelectionsInit) return null

  return (
    <>
      {optionsSorted?.map((option) => {
        if (!option?.uid || !option.attribute_code) return null
        const { attribute_code } = option
        return (
          <Controller
            key={option.uid}
            defaultValue={selection[attribute_code] ?? ''}
            name={`${name}[${attribute_code}]`}
            {...controlProps}
            render={() => <input type='hidden' />}
          />
        )
      })}
    </>
  )
}
